import { api, Shipment, Vehicle, exportShipmentsCSV } from '../api';
import { statusBadge, timeAgo, createFormModal, showModalError, filterPills, setupFilterPills } from '@demo/ui';
import type { FormField } from '@demo/ui';

const STATUSES = ['all', 'scheduled', 'loading', 'in_transit', 'delivered', 'cancelled'];
const CARGO_TYPES = ['Pharmaceuticals', 'Fresh Produce', 'Frozen Seafood', 'Dairy Products', 'Vaccines', 'Frozen Meat'];

export async function renderShipments(container: HTMLElement) {
  let shipments = await api.getShipments();
  let activeFilter = 'all';

  function tempCell(s: Shipment): string {
    if (s.current_temp == null) return '<span class="text-th-text-3">—</span>';
    const outOfRange = s.current_temp < s.temp_min || s.current_temp > s.temp_max;
    return `<span class="${outOfRange ? 'text-red-400 font-medium' : 'text-green-400'}">${s.current_temp.toFixed(1)}°C</span>`;
  }

  function renderTable(filter: string) {
    const filtered = filter === 'all' ? shipments : shipments.filter(s => s.status === filter);

    const cards = container.querySelector('#shipment-cards')!;
    if (filtered.length === 0) {
      cards.innerHTML = `<div class="text-center py-12 text-th-text-3">No shipments found</div>`;
    } else {
      cards.innerHTML = filtered.map(s => `
        <div class="bg-th-card rounded-xl border border-th-border p-4 cursor-pointer active:bg-th-card-hover transition-colors" data-card-id="${s.id}">
          <div class="flex items-center justify-between mb-2">
            <span class="text-blue-400 font-semibold text-sm">${s.id}</span>
            ${statusBadge(s.status)}
          </div>
          <div class="flex items-center gap-1.5 text-sm mb-2">
            <span class="text-th-text-2">${s.origin}</span>
            <svg class="w-3 h-3 text-th-text-4 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M14 5l7 7m0 0l-7 7m7-7H3"/></svg>
            <span class="text-th-text-2">${s.destination}</span>
          </div>
          <div class="flex items-center gap-4 text-xs text-th-text-3">
            <span>${s.cargo_type}</span>
            <span class="font-medium">${tempCell(s)}</span>
            <span class="ml-auto">${s.eta ? new Date(s.eta).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' }) : ''}</span>
          </div>
        </div>
      `).join('');
      cards.querySelectorAll('[data-card-id]').forEach(card => {
        card.addEventListener('click', () => {
          window.location.hash = `#/shipments/${(card as HTMLElement).dataset.cardId}`;
        });
      });
    }

    const tbody = container.querySelector('#shipment-tbody')!;
    if (filtered.length === 0) {
      tbody.innerHTML = `<tr><td colspan="7" class="px-6 py-12 text-center text-th-text-3">No shipments found</td></tr>`;
      return;
    }
    tbody.innerHTML = filtered.map(s => `
      <tr class="border-b border-th-border hover:bg-blue-500/[0.04] cursor-pointer transition-all" data-id="${s.id}">
        <td class="px-4 lg:px-6 py-4">
          <span class="text-blue-400 font-semibold text-sm">${s.id}</span>
        </td>
        <td class="px-4 lg:px-6 py-4">
          <div class="flex items-center gap-2 text-sm">
            <span class="text-th-text-2">${s.origin}</span>
            <svg class="w-3.5 h-3.5 text-th-text-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M14 5l7 7m0 0l-7 7m7-7H3"/></svg>
            <span class="text-th-text-2">${s.destination}</span>
          </div>
        </td>
        <td class="px-4 lg:px-6 py-4 text-th-text-3 text-sm">${s.cargo_type}</td>
        <td class="px-4 lg:px-6 py-4">${statusBadge(s.status)}</td>
        <td class="px-4 lg:px-6 py-4 text-sm font-medium">${tempCell(s)}</td>
        <td class="px-4 lg:px-6 py-4 text-th-text-3 text-sm">${s.eta ? new Date(s.eta).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' }) : '—'}</td>
        <td class="px-4 lg:px-6 py-4 text-th-text-3 text-sm">${s.vehicle_name || '—'}</td>
      </tr>
    `).join('');

    tbody.querySelectorAll('tr[data-id]').forEach(row => {
      row.addEventListener('click', () => {
        window.location.hash = `#/shipments/${(row as HTMLElement).dataset.id}`;
      });
    });
  }

  const filterItems = STATUSES.map(s => ({ id: s, label: s === 'all' ? 'All' : s.replace('_', ' ') }));

  container.innerHTML = `
    <div class="h-full overflow-y-auto page-enter">
      <div class="p-4 sm:p-8">
        <div class="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6 sm:mb-8">
          <div>
            <h1 class="text-xl sm:text-2xl font-bold tracking-tight">Shipments</h1>
            <p class="text-sm text-th-text-3 mt-1">${shipments.length} total shipments</p>
          </div>
          <div class="flex items-center gap-2 sm:gap-3">
            <button id="export-shipments-btn" class="flex items-center gap-2 px-3 sm:px-4 py-2 sm:py-2.5 bg-th-surface hover:bg-th-surface-hover border border-th-border-2 text-th-text-2 text-sm font-medium rounded-xl transition-all hover:border-th-border-2">
              <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4"/></svg>
              <span class="hidden sm:inline">Export CSV</span><span class="sm:hidden">CSV</span>
            </button>
            <button id="new-shipment-btn" class="flex items-center gap-2 px-3 sm:px-4 py-2 sm:py-2.5 bg-gradient-to-r from-blue-600 to-blue-500 hover:from-blue-500 hover:to-blue-400 text-white text-sm font-medium rounded-xl transition-all shadow-lg shadow-blue-500/20 hover:shadow-blue-500/30">
              <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4v16m8-8H4"/></svg>
              New
            </button>
          </div>
        </div>

        ${filterPills(filterItems, 'all')}

        <!-- Mobile card view -->
        <div class="sm:hidden space-y-3" id="shipment-cards"></div>

        <!-- Desktop table view -->
        <div class="hidden sm:block bg-th-card rounded-2xl border border-th-border overflow-hidden">
          <div class="overflow-x-auto">
            <table class="w-full min-w-[700px]">
              <thead>
                <tr class="border-b border-th-border">
                  <th class="px-4 lg:px-6 py-4 text-left text-[11px] font-semibold text-th-text-3 uppercase tracking-wider">ID</th>
                  <th class="px-4 lg:px-6 py-4 text-left text-[11px] font-semibold text-th-text-3 uppercase tracking-wider">Route</th>
                  <th class="px-4 lg:px-6 py-4 text-left text-[11px] font-semibold text-th-text-3 uppercase tracking-wider">Cargo</th>
                  <th class="px-4 lg:px-6 py-4 text-left text-[11px] font-semibold text-th-text-3 uppercase tracking-wider">Status</th>
                  <th class="px-4 lg:px-6 py-4 text-left text-[11px] font-semibold text-th-text-3 uppercase tracking-wider">Temp</th>
                  <th class="px-4 lg:px-6 py-4 text-left text-[11px] font-semibold text-th-text-3 uppercase tracking-wider">ETA</th>
                  <th class="px-4 lg:px-6 py-4 text-left text-[11px] font-semibold text-th-text-3 uppercase tracking-wider">Vehicle</th>
                </tr>
              </thead>
              <tbody id="shipment-tbody"></tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  `;

  renderTable('all');

  setupFilterPills(container, (filterId) => {
    activeFilter = filterId;
    renderTable(activeFilter);
  });

  container.querySelector('#export-shipments-btn')!.addEventListener('click', () => {
    exportShipmentsCSV(activeFilter !== 'all' ? activeFilter : undefined);
  });

  container.querySelector('#new-shipment-btn')!.addEventListener('click', async () => {
    const vehicles = await api.getVehicles();
    const modal = createFormModal({
      title: 'New Shipment',
      fields: [
        [
          { name: 'vehicle_id', label: 'Vehicle', type: 'select', required: true, options: vehicles.map(v => ({ value: v.id, label: `${v.name} (${v.id})` })) },
          { name: 'cargo_type', label: 'Cargo Type', type: 'select', required: true, options: CARGO_TYPES.map(c => ({ value: c, label: c })) },
        ],
        [{ name: 'cargo_description', label: 'Cargo Description', type: 'text', placeholder: 'e.g. Insulin pens, 2400 units' }],
        [
          { name: 'origin', label: 'Origin', type: 'text', required: true, placeholder: 'e.g. Istanbul' },
          { name: 'destination', label: 'Destination', type: 'text', required: true, placeholder: 'e.g. Munich' },
        ],
        [
          { name: 'temp_min', label: 'Temp Min (°C)', type: 'number', required: true, placeholder: '-25', step: '0.1' },
          { name: 'temp_max', label: 'Temp Max (°C)', type: 'number', required: true, placeholder: '8', step: '0.1' },
        ],
        [
          { name: 'humidity_min', label: 'Humidity Min (%)', type: 'number', placeholder: '40', step: '0.1' },
          { name: 'humidity_max', label: 'Humidity Max (%)', type: 'number', placeholder: '65', step: '0.1' },
        ],
        [
          { name: 'pickup_time', label: 'Pickup Time', type: 'datetime-local' },
          { name: 'eta', label: 'ETA', type: 'datetime-local' },
        ],
      ],
      submitLabel: 'Create Shipment',
      onSubmit: async (data) => {
        try {
          const body: Record<string, unknown> = {
            vehicle_id: data.vehicle_id,
            origin: data.origin,
            destination: data.destination,
            cargo_type: data.cargo_type,
            cargo_description: data.cargo_description || '',
            temp_min: parseFloat(data.temp_min),
            temp_max: parseFloat(data.temp_max),
          };
          if (data.humidity_min) body.humidity_min = parseFloat(data.humidity_min);
          if (data.humidity_max) body.humidity_max = parseFloat(data.humidity_max);
          if (data.pickup_time) body.pickup_time = new Date(data.pickup_time).toISOString();
          if (data.eta) body.eta = new Date(data.eta).toISOString();
          await api.createShipment(body);
          modal.remove();
          shipments = await api.getShipments();
          renderTable(activeFilter);
        } catch (err: any) {
          showModalError(modal, err.message);
        }
      },
      onClose: () => {},
    });
    document.body.appendChild(modal);
  });
}
